import { useCallback } from 'react'

import { useTheme } from 'next-themes'
import randomcolor from 'randomcolor'

import { getCompanyName } from '@/components/library-v2/companies/lib'

import { useActiveCompanyId } from '@/hooks/library/useActiveCompanyId'
import { useCompany } from '@/hooks/library/useCompanies'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganization } from '@/hooks/organization/useOrganization'

import type { UUID } from '@/types/Id'
import type { EntityCompany } from '@/types/library/Entity'
import type { Organization } from '@/types/Organization'

interface UseThemeAdjustedCompanyColorProps {
  company: EntityCompany
}

export const useThemeAdjustedCompanyColor = ({
  company,
}: UseThemeAdjustedCompanyColorProps) => {
  const { theme } = useTheme()
  return randomcolor({
    seed: getCompanyName(company),
    luminosity: theme === 'dark' ? 'dark' : 'light',
  })
}

const selectLinkedOrganizationId = (company: EntityCompany) => {
  return company.linked_organization_id
}

export const useIsOwnCompanyActive = () => {
  const { activeOrgId } = useActiveOrgId()
  const { activeCompanyId } = useActiveCompanyId()

  const { data: linkedOrganizationId } = useCompany({
    orgId: activeOrgId,
    companyId: activeCompanyId,
    select: selectLinkedOrganizationId,
  })

  return !!linkedOrganizationId && linkedOrganizationId === activeOrgId
}

interface UseIsOwnCompanyProps {
  companyId: UUID
}

export const useIsOwnCompany = ({ companyId }: UseIsOwnCompanyProps) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: linkedOrganizationId } = useCompany({
    orgId: activeOrgId,
    companyId: companyId,
    select: selectLinkedOrganizationId,
  })
  return !!linkedOrganizationId && linkedOrganizationId === activeOrgId
}

interface UseMergedCompanyProps {
  orgId: UUID | ''
  companyId: UUID | ''
}

interface UseMergedCompanyResult {
  /** The merged company data (org values take precedence for synced fields when isOwnCompany) */
  mergedCompany: EntityCompany | null
  /** The original company data (unmerged) */
  company: EntityCompany | undefined
  /** The organization data (only fetched if isLinked) */
  organization: Organization | undefined
  /** Whether this company is linked to the organization specified by orgId */
  isLinked: boolean
  /** Loading state for company and organization data */
  isLoading: boolean
}

/**
 * Hook that returns company data merged with organization data when viewing your own company.
 * Organization values take precedence for synced fields (name, uei, cage_code, website_url).
 * Note: description is NOT merged because company uses JSON (tiptap) and org uses markdown.
 */
export const useMergedCompany = ({
  orgId,
  companyId,
}: UseMergedCompanyProps): UseMergedCompanyResult => {
  const { data: organization, isLoading: orgLoading } = useOrganization(orgId)

  const selectMergedCompany = useCallback(
    (company: EntityCompany) => {
      if (!organization) return company
      // ⚠️  🚨 CRITICAL WARNING - DO NOT REMOVE THIS CHECK! 🚨 ⚠️
      // This ensures we only merge company data with organization data
      // when the company is actually linked to the organization
      if (company.linked_organization_id !== orgId) return company
      return {
        ...company,
        name: organization.name ?? company.name,
        uei: organization.unique_entity_id ?? company.uei,
        cage_code: organization.cage_code ?? company.cage_code,
        website_url: organization.website_url ?? company.website_url,
        // NOTE: Don't merge description - company uses JSON format (tiptap),
        // org uses markdown format. They are synced during save operations.
      }
    },
    [organization],
  )

  const { data: company, isLoading: companyLoading } = useCompany({
    orgId,
    companyId: companyId as UUID,
    select: selectMergedCompany,
  })

  const { data: originalCompany, isLoading: originalCompanyLoading } =
    useCompany({
      orgId,
      companyId: companyId as UUID,
    })

  const isLinked =
    !!company?.linked_organization_id &&
    company.linked_organization_id === orgId

  const isLoading =
    companyLoading || originalCompanyLoading || (isLinked && orgLoading)

  return {
    mergedCompany: company ?? null,
    company: originalCompany,
    organization,
    isLinked,
    isLoading,
  }
}
